/**
 * Verrou navigateur d'une bascule de session sur un sous-domaine tenant.
 *
 * Le cookie de session est partagé par tous les onglets d'une même origine.
 * Pendant un handoff, un onglet ancien ne doit donc ni ajouter une opération à
 * l'outbox ni la pousser après le remplacement du cookie. Le verrou vit dans
 * localStorage afin d'être immédiatement visible par les autres onglets.
 *
 * Ce verrou n'est pas une autorité serveur. Il protège seulement la courte
 * section critique locale validation → contrôle outbox → consume → purge. Une
 * expiration bornée évite de figer le terrain si l'onglet plante.
 */

const CLE_VERROU = "champ-erp:session-handoff-lock";
const CLE_NOTIFICATION = "champ-erp:session-handoff-complete";
const NOM_VERROU_WEB = "champ-erp:session-data";
const DUREE_MAX_VERROU_MS = 2 * 60_000;
const PREFIXE_OPERATIONS_PARTAGEES = "champ-erp:session-writer:";
// Une requête terrain sur mobile peut rester longtemps suspendue en arrière-
// plan. Dix minutes évitent qu'un simple throttling transforme sa lease en
// faux orphelin pendant une bascule de session.
const DUREE_LEASE_OPERATION_MS = 10 * 60_000;
const RENOUVELLEMENT_LEASE_MS = 15_000;
const ATTENTE_OPERATION_MS = 25;

const operationsLocalesActives = new Set<string>();
let documentSessionId: string | null = null;
let ecouteFermetureInstallee = false;

interface VerrouStocke {
  id: string;
  expireLe: number;
}

export class BasculeSessionEnCoursError extends Error {
  constructor() {
    super("Une connexion est en cours dans un autre onglet. Réessayez dans quelques secondes.");
    this.name = "BasculeSessionEnCoursError";
  }
}

export class BasculeSessionOngletBloqueError extends BasculeSessionEnCoursError {
  constructor() {
    super();
    this.message =
      "Une opération reste ouverte dans un autre onglet Crayéa. Fermez les autres onglets, puis rechargez cette page avant de réessayer.";
    this.name = "BasculeSessionOngletBloqueError";
  }
}

/** Demande de conserver le verrou local après un remplacement de cookie incertain. */
export class BasculeSessionIncertitudeError extends Error {
  constructor(public readonly erreurInitiale: unknown) {
    super("La nouvelle session n'a pas pu être sécurisée localement.");
    this.name = "BasculeSessionIncertitudeError";
  }
}

function lireVerrou(): VerrouStocke | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const brut = localStorage.getItem(CLE_VERROU);
    if (!brut) return null;
    const candidat = JSON.parse(brut) as Partial<VerrouStocke>;
    if (
      typeof candidat.id !== "string" ||
      typeof candidat.expireLe !== "number" ||
      candidat.expireLe <= Date.now()
    ) {
      localStorage.removeItem(CLE_VERROU);
      return null;
    }
    return { id: candidat.id, expireLe: candidat.expireLe };
  } catch {
    // Un stockage corrompu ne doit pas bloquer indéfiniment le terrain.
    try {
      localStorage.removeItem(CLE_VERROU);
    } catch {
      // Stockage privé indisponible : le caller continue en mode mono-onglet.
    }
    return null;
  }
}

/** Vrai pendant la section critique d'un handoff sur cette origine. */
export function basculeSessionEnCours(): boolean {
  return lireVerrou() !== null;
}

/** Refuse une écriture/synchronisation locale pendant la bascule. */
export function verifierAbsenceBasculeSession(): void {
  if (basculeSessionEnCours()) throw new BasculeSessionEnCoursError();
}

export interface VerrouBasculeSession {
  liberer: () => void;
}

interface VerrouOperationSession {
  liberer: () => void;
}

function webLocksDisponible(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.locks?.request === "function";
}

function obtenirDocumentSessionId(): string {
  // Ne jamais persister cet identifiant dans sessionStorage : le navigateur
  // peut le cloner lors d'une duplication d'onglet. Chaque contexte JS doit
  // rester propriétaire de ses seules leases en mémoire.
  documentSessionId ??= crypto.randomUUID();
  return documentSessionId;
}

interface LeaseOperationStockee {
  id: string;
  documentSessionId: string;
  expireLe: number;
}

function lireLeaseOperation(key: string): LeaseOperationStockee | null {
  try {
    const lease = JSON.parse(
      localStorage.getItem(key) ?? "null",
    ) as Partial<LeaseOperationStockee> | null;
    if (
      typeof lease?.id !== "string" ||
      typeof lease.documentSessionId !== "string" ||
      typeof lease.expireLe !== "number"
    ) {
      return null;
    }
    return {
      id: lease.id,
      documentSessionId: lease.documentSessionId,
      expireLe: lease.expireLe,
    };
  } catch {
    return null;
  }
}

function retirerOperationsLocales(): void {
  for (const key of operationsLocalesActives) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Le prochain chargement du même onglet tentera à nouveau le nettoyage.
    }
  }
  operationsLocalesActives.clear();
}

/**
 * Installe le nettoyage des seules leases détenues par ce contexte JS.
 *
 * Une navigation détruite (`persisted=false`) ne reprendra pas son opération.
 * Une page conservée en BFCache garde en revanche sa lease : elle peut être
 * restaurée avec son contexte et ne doit pas laisser une bascule passer.
 */
function preparerCoordinationLocale(): void {
  if (typeof localStorage === "undefined") return;
  if (!ecouteFermetureInstallee && typeof window !== "undefined") {
    window.addEventListener("pagehide", (event) => {
      if (!event.persisted) retirerOperationsLocales();
    });
    ecouteFermetureInstallee = true;
  }
}

/**
 * Retourne toutes les leases de writers qui n'ont pas été explicitement
 * libérées.
 *
 * Une date expirée ne prouve pas qu'un writer est terminé : sur mobile, les
 * timers d'un onglet en arrière-plan peuvent être suspendus bien plus de
 * 30 secondes alors que sa requête réseau reste en vol. Pendant une bascule,
 * le fallback doit donc échouer fermé et attendre la libération explicite.
 */
function listerOperationsSessionActives(): Array<{
  key: string;
  expireLe: number | null;
}> {
  const actives: Array<{ key: string; expireLe: number | null }> = [];
  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (!key?.startsWith(PREFIXE_OPERATIONS_PARTAGEES)) continue;
    const lease = lireLeaseOperation(key);
    // Une entrée corrompue sous notre préfixe est aussi traitée comme un
    // writer indéterminé : l'effacer ouvrirait une course cross-session.
    actives.push({ key, expireLe: lease?.expireLe ?? null });
  }
  return actives;
}

/**
 * Fallback coopératif pour les navigateurs sans Web Locks.
 *
 * Le writer publie sa lease AVANT l'opération puis recontrôle le verrou de
 * handoff. L'exclusif pose d'abord son verrou et attend toutes les leases :
 * les deux ordres de course sont ainsi fermés sans laisser un ancien writer
 * écrire après la purge.
 */
function verrouillerOperationSessionLocale(): VerrouOperationSession {
  if (typeof localStorage === "undefined") throw new BasculeSessionEnCoursError();
  preparerCoordinationLocale();
  verifierAbsenceBasculeSession();

  const id = crypto.randomUUID();
  const contexteId = obtenirDocumentSessionId();
  const key = `${PREFIXE_OPERATIONS_PARTAGEES}${id}`;
  const renouveler = () => {
    localStorage.setItem(
      key,
      JSON.stringify({
        id,
        documentSessionId: contexteId,
        expireLe: Date.now() + DUREE_LEASE_OPERATION_MS,
      }),
    );
  };
  renouveler();
  operationsLocalesActives.add(key);

  // Si l'exclusif s'est installé entre le premier contrôle et l'écriture de la
  // lease, le writer se retire sans commencer son opération.
  try {
    verifierAbsenceBasculeSession();
  } catch (cause) {
    operationsLocalesActives.delete(key);
    localStorage.removeItem(key);
    throw cause;
  }

  const intervalle = globalThis.setInterval(renouveler, RENOUVELLEMENT_LEASE_MS);
  return {
    liberer: () => {
      globalThis.clearInterval(intervalle);
      operationsLocalesActives.delete(key);
      try {
        localStorage.removeItem(key);
      } catch {
        // Fail-closed : une lease non retirée empêchera la prochaine bascule.
      }
    },
  };
}

async function attendreFinOperationsSession(): Promise<void> {
  const limite = Date.now() + DUREE_MAX_VERROU_MS;
  while (true) {
    const operations = listerOperationsSessionActives();
    if (operations.length === 0) return;
    // Une lease expirée/corrompue ne prouve pas que l'onglet est mort. On ne
    // la supprime jamais automatiquement et on explique immédiatement comment
    // sortir du mode dégradé.
    if (operations.some(({ expireLe }) => expireLe === null || expireLe <= Date.now())) {
      throw new BasculeSessionOngletBloqueError();
    }
    if (Date.now() >= limite) throw new BasculeSessionEnCoursError();
    await new Promise<void>((resolve) => globalThis.setTimeout(resolve, ATTENTE_OPERATION_MS));
  }
}

/**
 * Prend le verrou inter-onglets. Le contrôle après écriture ferme la majorité
 * des courses localStorage ; le backend et le contrôle d'outbox restent les
 * gardes déterminantes si deux handoffs démarrent exactement au même instant.
 */
export function verrouillerBasculeSession(): VerrouBasculeSession {
  if (typeof localStorage === "undefined") return { liberer: () => undefined };
  if (lireVerrou()) throw new BasculeSessionEnCoursError();

  const verrou: VerrouStocke = {
    id: crypto.randomUUID(),
    expireLe: Date.now() + DUREE_MAX_VERROU_MS,
  };
  localStorage.setItem(CLE_VERROU, JSON.stringify(verrou));
  if (lireVerrou()?.id !== verrou.id) throw new BasculeSessionEnCoursError();

  return {
    liberer: () => {
      try {
        if (lireVerrou()?.id === verrou.id) localStorage.removeItem(CLE_VERROU);
      } catch {
        // Le TTL rend un échec de libération récupérable.
      }
    },
  };
}

/**
 * Exécute une écriture/synchronisation locale sous verrou PARTAGÉ.
 *
 * Le Web Lock ferme la course « writer contrôlé → handoff verrouillé → writer
 * écrit trop tard » entre onglets. Le contrôle localStorage reste une défense
 * visible et le repli pour les navigateurs sans Web Locks.
 */
export async function executerOperationSessionLocale<T>(operation: () => Promise<T>): Promise<T> {
  const executer = async () => {
    verifierAbsenceBasculeSession();
    return operation();
  };
  if (webLocksDisponible()) {
    return navigator.locks.request(NOM_VERROU_WEB, { mode: "shared" }, executer);
  }

  const verrou = verrouillerOperationSessionLocale();
  try {
    return await operation();
  } finally {
    verrou.liberer();
  }
}

/**
 * Exécute toute la section critique d'un handoff sous verrou EXCLUSIF.
 *
 * Les écritures/synchronisations déjà parties terminent avant l'acquisition ;
 * les suivantes restent en attente jusqu'à la fin. Une fois acquis, le verrou
 * localStorage informe aussi les chemins legacy et les autres onglets.
 */
export async function executerBasculeSessionExclusive<T>(operation: () => Promise<T>): Promise<T> {
  const executer = async () => {
    if (!webLocksDisponible()) preparerCoordinationLocale();
    const verrou = verrouillerBasculeSession();
    let conserverVerrou = false;
    try {
      // Sans Web Locks, les writers déjà commencés ont publié une lease. Le
      // verrou local empêche les nouveaux de partir pendant qu'on les attend.
      if (!webLocksDisponible()) await attendreFinOperationsSession();
      return await operation();
    } catch (erreur) {
      if (erreur instanceof BasculeSessionIncertitudeError) conserverVerrou = true;
      throw erreur;
    } finally {
      // Si le cookie a changé et que ni la purge ni le logout ne sont sûrs,
      // bloquer les writers jusqu'au TTL est plus sûr que pousser sous une
      // identité indéterminée. Le Web Lock est libéré par le navigateur, mais
      // tous nos writers recontrôlent ce verrou local avant d'agir.
      if (!conserverVerrou) verrou.liberer();
    }
  };
  if (!webLocksDisponible()) return executer();
  return navigator.locks.request(NOM_VERROU_WEB, { mode: "exclusive" }, executer);
}

/** Avertit les autres onglets que le cookie vient de changer. */
export function notifierBasculeSessionTerminee(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(CLE_NOTIFICATION, crypto.randomUUID());
  } catch {
    // Best effort : le nouvel onglet reste sûr, les anciens seront rejetés par l'API.
  }
}

/** Recharge les autres onglets afin qu'ils relisent identité et permissions. */
export function installerEcouteBasculeSessionTerminee(
  recharger: () => void = () => window.location.reload(),
): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handler = (event: StorageEvent) => {
    if (event.key === CLE_NOTIFICATION && event.newValue) recharger();
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}

export const SESSION_HANDOFF_LOCK_STORAGE_KEY = CLE_VERROU;
